/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';

import {
  setCurrentScrollPerfectScrollbarTablePosition,
  clearMovingScrollPerfectScrollbarTableToPosition,
} from 'src/actions/scrollActions';

import helpers from 'src/helpers';
import { computeScrollBoxHeight } from 'src/utils/scrollBoxHeight';

// CON-1656 — the bulk action bar is a Drawer whose paper is position: fixed
// (MUI applies that to every variant, persistent included), so nothing in
// normal flow can reserve space for it and it floated over the pagination row
// below this scroller. Bottom padding cannot fix that: the pagination is a
// SIBLING of this scroller, not a child, and this element's height is derived
// from viewport geometry rather than from available space.
//
// The bar is measured and subtracted here instead, exactly as the pagination
// row and the page header already are. Pages with no bulk bar find no element
// and subtract 0, so this is a no-op for them.
const BULK_BAR_ELEMENT_ID = 'bulkOperationsBar';

/**
 * CON-1656 — recompute the table height as the page scrolls.
 *
 * THE TRADE-OFF, recorded so this can be reversed on purpose:
 *
 *   true  — the table reclaims space as the header and toolbar scroll off,
 *           so it grows on the way down and shrinks on the way back up.
 *           Matters most at 125-150% browser zoom, where the fixed chrome
 *           eats a large share of a short viewport. Cost: the table height
 *           is no longer stable while scrolling.
 *
 *   false — the table keeps one height for the whole session. Stable and
 *           predictable, but whatever the chrome takes is gone for good.
 *
 * Flip this single constant to disable; nothing else needs touching.
 */
const RECOMPUTE_HEIGHT_ON_PAGE_SCROLL = false;

function PerfectScrollbarTable({
  children,
  subDefaultHeight,
  scrollBoxId,
}) {
  const dispatch = useDispatch();
  const scrollRef = useRef(null);

  const [scrollHeight, setScrollHeight] = useState('none');

  const {
    currentScrollPerfectScrollbarTablePosition,
    moveScrollPerfectScrollbarTableToPosition,
  } = useSelector((state) => state?.scroll || {});

  const {
    shouldSave,
  } = currentScrollPerfectScrollbarTablePosition || {};

  const {
    x: moveToX,
    y: moveToY,
    shouldStart: shouldStartMoveTo,
    perfectScrollbarId = 'scrollBox',
  } = moveScrollPerfectScrollbarTableToPosition || {};

  const getScrollMaxHeightOnWindow = () => {
    const heightW = helpers.transform.toInt10(document.documentElement.clientHeight);

    const scrollBoxEl = document.getElementById('scrollBox');
    const paginationEl = document.getElementById('tablePagination');
    const headerEl = document.getElementById('header-top-right');
    const bulkBarEl = document.getElementById(BULK_BAR_ELEMENT_ID);

    const scrollBoxOffsetTop = helpers.transform.toInt10(scrollBoxEl?.offsetTop || '0');
    const paginationHeight = helpers.transform.toInt10(paginationEl?.offsetHeight || '0');
    const headerHeight = helpers.transform.toInt10(headerEl?.offsetHeight || '0');
    const bulkBarRect = bulkBarEl ? bulkBarEl.getBoundingClientRect() : null;
    // Where the scroll box ACTUALLY starts in the viewport. offsetTop above is
    // relative to a positioned ancestor and runs ~276px short here; see the
    // unit-mismatch note in scrollBoxHeight.js. Used only when a bulk bar is
    // on screen, and only to shrink the result.
    const scrollBoxViewportTop = scrollBoxEl
      ? scrollBoxEl.getBoundingClientRect().top
      : null;

    return computeScrollBoxHeight({
      viewportHeight: heightW,
      scrollBoxOffsetTop,
      scrollBoxViewportTop,
      paginationHeight,
      headerHeight,
      bulkBarRect,
      subDefaultHeight,
    });
  };

  useEffect(() => {
    const updateScrollHeight = () => {
      setScrollHeight(getScrollMaxHeightOnWindow());
    };
    updateScrollHeight();
    window.addEventListener('resize', updateScrollHeight);

    // The bulk bar changes height WITHOUT a window resize — swapping between
    // the one-line hint and the action buttons on first selection, and back
    // again on deselect. A window listener alone would miss both. Observing
    // the element covers those plus button wrap at narrow widths, since any
    // of the three changes its measured height.
    //
    // ResizeObserver fires once on observe() for a rendered element, so this
    // also re-measures after the bar has mounted, without a second timer.
    const bulkBarEl = document.getElementById(BULK_BAR_ELEMENT_ID);
    let bulkBarObserver = null;

    // Page scroll moves the scroll box up the viewport, which grows the space
    // below it. scrollBoxViewportTop is a live measurement, so re-running the
    // calculation is all that is needed — but nothing was re-running it.
    //
    // Capture phase on document, NOT window: the page scrolls on an inner
    // div in DashboardLayout (classes.content, overflow: auto), so a window
    // scroll listener never fires here.
    //
    // rAF-throttled because scroll fires continuously and this reads layout.
    let scrollFrame = null;
    const onAnyScroll = () => {
      if (scrollFrame !== null) return;
      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = null;
        updateScrollHeight();
      });
    };

    if (RECOMPUTE_HEIGHT_ON_PAGE_SCROLL) {
      document.addEventListener('scroll', onAnyScroll, true);
    }

    if (bulkBarEl && typeof ResizeObserver !== 'undefined') {
      bulkBarObserver = new ResizeObserver(updateScrollHeight);
      bulkBarObserver.observe(bulkBarEl);
    }

    return () => {
      window.removeEventListener('resize', updateScrollHeight);
      document.removeEventListener('scroll', onAnyScroll, true);
      if (scrollFrame !== null) {
        window.cancelAnimationFrame(scrollFrame);
      }
      if (bulkBarObserver) {
        bulkBarObserver.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    // call dispatch(saveCurrentScrollDashboardLayoutContentPosition(true));
    // dispatch(saveCurrentScrollPerfectScrollbarTablePosition(true));
    // if you want to save current position
    if (shouldSave && scrollRef) {
      const x = helpers.transform.toInt10(scrollRef.current.scrollLeft);
      const y = helpers.transform.toInt10(scrollRef.current.scrollTop);
      dispatch(setCurrentScrollPerfectScrollbarTablePosition(x, y));
    }
  }, [shouldSave, scrollRef]);

  useEffect(() => {
    if (shouldStartMoveTo && perfectScrollbarId === scrollBoxId && scrollRef) {
      scrollRef.current.scrollLeft = helpers.transform.toInt10(moveToX || 0);
      scrollRef.current.scrollTop = helpers.transform.toInt10(moveToY || 0);
      dispatch(clearMovingScrollPerfectScrollbarTableToPosition());
    }
  }, [shouldStartMoveTo, scrollRef, perfectScrollbarId, scrollBoxId]);

  return (
    <PerfectScrollbar
      id={scrollBoxId || 'scrollBox'}
      style={{ maxHeight: scrollHeight }}
      containerRef={(el) => { scrollRef.current = el; }}
    >
      {children}
    </PerfectScrollbar>
  );
}

PerfectScrollbarTable.propTypes = {
  children: PropTypes.any.isRequired,
  subDefaultHeight: PropTypes.number,
  scrollBoxId: PropTypes.string,
};

PerfectScrollbarTable.defaultProps = {
  subDefaultHeight: 0,
  scrollBoxId: 'scrollBox',
};

export default PerfectScrollbarTable;
